<template>
  <div v-if="User.info">
    <div
      :class="{ hidden: !modal.active }"
      class="fixed flex justify-center lg:justify-end items-start top-0 ltr:left-0 rtl:right-0 z-50 bg-gray-600 bg-opacity-30 w-full h-full overflow-y-auto"
    >
      <div class="w-[95%] max-w-[1539px]">
        <div class="h-[70px] w-full">
          <button class="w-[40px] h-[40px] rounded-3xl bg-white p-[8px] mt-[15px] ltr:float-right ltr:lg:float-left rtl:float-left rtl:lg:float-right" @click="closeModal()">
            <img width="24px" height="24px" src="../assets/img/icons/close-blue.png" />
          </button>
        </div>
        <div class="lg:pt-12 lg:px-16 bg-white rounded-xl ltr:lg:rounded-r-none rtl:lg:rounded-l-none mb-5">
          <div class="w-full p-5 pb-0 lg:px-0">
            <div class="flex flex-col mt-5 md:mt-0 md:flex-row justify-start md:justify-between">
              <h2 class="w-full lg:w-auto font-display text-3xl font-bold pt-[3px] text-gray-900">{{ $t('view_trades').tTitle() }}</h2>
              <div class="w-full lg:w-[200px] ltr:lg:text-right rtl:lg:text-left mt-5 lg:mt-0 flex flex-col items-start md:items-end">
                <p class="font-body text-sm text-gray-900">{{ $t('account_number').tTitle() }}</p>
                <p class="font-body text-5xl md:text-3xl text-blue font-bold">{{ TAID }}</p>
                <button
                  @click="exportToCSV()"
                  class="block mt-8 lg:mt-1 bg-blue text-white font-body font-bold w-1/2 lg:w-[162px] h-[42px] text-center rounded lg:ml-2 leading-[100%]"
                >
                  {{ $t('export_csv').tTitle() }}
                </button>
              </div>
            </div>
          </div>
          <div class="lg:hidden flex justify-end" :class="{'border-b border-b-gray-300': !searchFiltersOpen}">
            <button
              type="button"
              @click="toggleSearchFilters"
              class="flex items-center bg-white rounded-tl justify-center gap-2 px-5 py-4 text-blue font-body"
              :class="{'!bg-gray-100': searchFiltersOpen}"
              :aria-expanded="searchFiltersOpen ? 'true' : 'false'"
            >
              {{ $t('search').tTitle() }}
              <img
                width="12px"
                height="12px"
                class="transition-transform duration-300"
                :class="{ 'rotate-180': searchFiltersOpen }"
                src="../assets/img/platform-dropdown.svg"
              />
            </button>
          </div>
          <div class="flex flex-wrap lg:justify-between w-full bg-white lg:mt12">
            <div class="flex bg-gray-400 w-full lg:w-[370px] h-[42px] rounded-3xl text-gray-200 mt-9 lg:mt-4 order-last lg:order-1 m-5 lg:m-1">
              <div
                @click="loadTrades('openTrades')"
                class="w-1/2 text-center leading-10 cursor-pointer"
                :class="[selectedTradesType === 'openTrades' ? 'text-white text-bold bg-blue rounded-3xl' : 'text-gray-900']"
              >
                {{ $t('open_trades').tTitle() }}
              </div>
              <div
                @click="loadTrades('tradeHistory')"
                class="w-1/2 text-center leading-10 cursor-pointer"
                :class="[selectedTradesType === 'tradeHistory' ? 'text-white text-bold bg-blue rounded-3xl' : 'text-gray-900']"
              >
                {{ $t('closed_trades').tTitle() }}
              </div>
            </div>
            <div
              class="lg:flex rounded w-full lg:w-auto order-2 bg-gray-100 lg:bg-white overflow-hidden lg:overflow-visible transition-all duration-300 ease-in-out lg:max-h-none lg:opacity-100 lg:mt-4 lg:p-0 lg:rounded-lg"
              :class="searchFiltersOpen ? 'max-h-[360px] opacity-100 p-5' : 'max-h-0 opacity-0 mt-0 p-0'"
            >
              <label class="block font-body leading-7 md:leading-10 mt-[10px] md:mt-1 ltr:mr-4 rtl:ml-4 whitespace-nowrap text-gray-900">{{ $t('date_from').tTitle() }}</label>
              <date-picker
                class="lg:!w-44 !w-full"
                value-type="format"
                format="YYYY-MM-DD"
                v-model="dateFrom"
                :lang="Language"
                name="dateFrom"
                :disabled-date="disabledDate"
                :rtl="Language === 'fa'"
                @change="onDateFromChange"
              ></date-picker>
              <label class="block font-body leading-7 md:leading-10 mt-5 md:mt-1 ltr:ml-0 ltr:md:ml-6 rtl:mr-0 rtl:md:mr-4 ltr:mr-4 rtl:ml-4 whitespace-nowrap text-gray-900">{{
                $t('date_until').tTitle()
              }}</label>
              <date-picker
                class="lg:!w-44 !w-full"
                value-type="format"
                format="YYYY-MM-DD"
                v-model="dateTo"
                :lang="Language"
                name="dateTo"
                :rtl="Language === 'fa'"
                :disabled-date="disabledDateTo"
              ></date-picker>
              <button
                @click="getTradeHistory()"
                class="block mt-[30px] mb-[10px] lg:mt-0 bg-blue text-white font-body font-bold w-[160px] h-[50px] text-center rounded ltr:lg:ml-5 rtl:lg:mr-5 leading-[100%]"
              >
                {{ $t('search').tTitle() }}
              </button>
            </div>
          </div>
          <div class="w-full">
            <table class="hidden lg:table w-full mt-5 border-collapse">
              <thead class="font-body text-blue font-bold ltr:text-left rtl:text-right">
                <tr class="h-[50px]">
                  <th class="ltr:pl-2 rtl:pr-2">{{ $t('trade_no').tTitle() }}</th>
                  <th class="cursor-pointer ltr:text-left rtl:text-right">
                    <button
                      class="inline-flex items-center"
                      @click="changeOrderBy('date')"
                      :aria-sort="tradeFields.orderBy === 'date' ? (tradeFields.orderMode === 'asc' ? 'ascending' : 'descending') : 'none'"
                    >
                      {{ $t('datetime').tTitle() }}
                      <img
                        class="w-3 h-2 ltr:ml-1.5 rtl:mr-1.5 transition-transform"
                        :class="tradeFields.orderBy === 'date' && tradeFields.orderMode === 'asc' ? 'rotate-180' : 'rotate-0'"
                        src="../assets/img/platform-dropdown.svg"
                      />
                    </button>
                  </th>
                  <th class="ltr:text-right rtl:text-left min-w-[5rem] ltr:pr-6 rtl:pl-6 whitespace-nowrap">{{ $t('type').tTitle() }}</th>
                  <th class="ltr:text-right rtl:text-left min-w-[6rem] ltr:pr-4 rtl:pl-4 whitespace-nowrap">{{ $t('symbol').tTitle() }}</th>
                  <th class="cursor-pointer ltr:text-right rtl:text-left">
                    <button
                      class="inline-flex items-center"
                      @click="changeOrderBy('volume')"
                      :aria-sort="tradeFields.orderBy === 'volume' ? (tradeFields.orderMode === 'asc' ? 'ascending' : 'descending') : 'none'"
                    >
                      {{ $t('volume').tTitle() }}
                      <img
                        class="w-3 h-2 ltr:ml-1.5 rtl:mr-1.5 transition-transform"
                        :class="tradeFields.orderBy === 'volume' && tradeFields.orderMode === 'asc' ? 'rotate-180' : 'rotate-0'"
                        src="../assets/img/platform-dropdown.svg"
                      />
                    </button>
                  </th>
                  <th class="ltr:text-right rtl:text-left">{{ $t('open_price').tTitle() }}</th>
                  <th class="ltr:text-right rtl:text-left">{{ $t('close_price').tTitle() }}</th>
                  <th class="ltr:text-right rtl:text-left">{{ $t('swap').tTitle() }}</th>
                  <th class="ltr:text-right rtl:text-left">{{ $t('commission').tTitle() }}</th>
                  <th class="cursor-pointer ltr:text-right rtl:text-left ltr:pr-2 rtl:pl-2">
                    <button
                      class="inline-flex items-center"
                      @click="changeOrderBy('P&L')"
                      :aria-sort="tradeFields.orderBy === 'P&L' ? (tradeFields.orderMode === 'asc' ? 'ascending' : 'descending') : 'none'"
                    >
                      {{ $t('p_l').tTitle() }}
                      <img
                        class="w-3 h-2 ltr:ml-1.5 rtl:mr-1.5 transition-transform"
                        :class="tradeFields.orderBy === 'P&L' && tradeFields.orderMode === 'asc' ? 'rotate-180' : 'rotate-0'"
                        src="../assets/img/platform-dropdown.svg"
                      />
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody class="font-body ltr:text-left rtl:text-right">
                <tr v-for="(trade, i) in trades" :key="i" :id="i" class="odd:bg-gray-400 even:bg-white h-[50px]">
                  <td class="ltr:rounded-l-xl rtl:rounded-r-xl ltr:pl-2 rtl:pr-2">{{ trade.order }}</td>
                  <td>{{ selectedTradesType == 'openTrades' ? trade.open_time_formatted : trade.close_time_formatted }}</td>
                  <td class="ltr:text-right rtl:text-left min-w-[5rem] ltr:pr-6 rtl:pl-6 whitespace-nowrap">
                    {{
                      selectedTradesType === 'tradeHistory' && platform === 'mt5'
                        ? trade.cmd == '1'
                          ? $t('buy').tTitle()
                          : trade.cmd == '0'
                            ? $t('sell').tTitle()
                            : $t('balance').tTitle()
                        : trade.cmd == '0'
                          ? $t('buy').tTitle()
                          : trade.cmd == '1'
                            ? $t('sell').tTitle()
                            : $t('balance').tTitle()
                    }}
                  </td>
                  <td class="ltr:text-right rtl:text-left min-w-[6rem] ltr:pr-4 rtl:pl-4 whitespace-nowrap">{{ $simplePair(trade.symbol) }}</td>
                  <td class="ltr:text-right rtl:text-left">{{ trade.true_volume }}</td>
                  <td class="ltr:text-right rtl:text-left">{{ trade.open_price }}</td>
                  <td class="ltr:text-right rtl:text-left">{{ trade.close_price }}</td>
                  <td class="ltr:text-right rtl:text-left">{{ trade.storage }}</td>
                  <td class="ltr:text-right rtl:text-left">{{ trade.commission }}</td>
                  <td class="ltr:rounded-r-xl rtl:rounded-l-xl ltr:text-right rtl:text-left ltr:pr-2 rtl:pl-2">{{ trade.true_profit.toFixed(2) }}</td>
                </tr>
                <tr class="odd:bg-gray-400 even:bg-white h-[50px]">
                  <td class="ltr:rounded-l-xl rtl:rounded-r-xl ltr:pl-2 rtl:pr-2 font-semibold text-gray-900">{{ $t('sums').tTitle() }}</td>
                  <td colspan="4" class="ltr:text-right rtl:text-left font-bold">{{ sums.true_volume ? sums.true_volume.toFixed(2) : 0 }}</td>
                  <td colspan="3" class="ltr:text-right rtl:text-left font-bold">{{ sums.storage ? sums.storage.toFixed(2) : 0 }}</td>
                  <td colspan="1" class="ltr:text-right rtl:text-left font-bold">{{ sums.commission ? sums.commission.toFixed(2) : 0 }}</td>
                  <td colspan="1" class="ltr:rounded-r-xl rtl:rounded-l-xl ltr:text-right rtl:text-left font-bold ltr:pr-2 rtl:pl-2">
                    {{ sums.true_profit ? sums.true_profit.toFixed(2) : 0 }}
                  </td>
                </tr>
                <tr v-if="this.ndbonus && selectedTradesType === 'tradeHistory'">
                  <td colspan="9" class="ltr:rounded-l-xl rtl:rounded-r-xl ltr:pl-2 font-semibold text-gray-900">
                    {{ $t('active_tr_days_nr').tTitle() }}
                  </td>
                  <td class="ltr:rounded-r-xl rtl:rounded-l-xl ltr:text-right rtl:text-left font-bold ltr:pr-[0.3rem]">{{ sums.active_days ? sums.active_days : 0 }}</td>
                </tr>
                <tr class="bg-white h-[50px]">
                  <td colspan="9" class="ltr:rounded-l-xl rtl:rounded-r-xl ltr:pl-2 rtl:pr-2 font-semibold text-gray-900">
                    {{ $t('net_total').tTitle() }}
                  </td>
                  <td class="ltr:text-right rtl:text-left font-bold">{{ sums.net_total ? Number(sums.net_total).toFixed(2) : 0 }}</td>
                </tr>
              </tbody>
            </table>
            <div class="w-full lg:hidden text-body p-5">
              <div
                v-for="(trade, i) in trades"
                :key="'m-' + i"
                :id="'m-' + i"
                class="flex justify-between px-4 pt-[13px] rounded-lg odd:bg-gray-400 overflow-hidden"
                :class="{ 'max-h-[50px]': !trade.expanded }"
                @click="expandTrade(i)"
              >
                <div class="text-blue font-bold">
                  <label class="block h-9">
                    <img
                      width="12px"
                      height="12px"
                      class="ltr:mr-1 rtl:ml-1 cursor-pointer inline"
                      :class="{ '-rotate-90': !trade.expanded }"
                      src="../assets/img/platform-dropdown.svg"
                    />
                    {{ $t('trade_no').tTitle() }}
                  </label>
                  <label class="block h-9">{{ $t('datetime').tTitle() }}</label>
                  <label class="block h-9">{{ $t('type').tTitle() }}</label>
                  <label class="block h-9">{{ $t('symbol').tTitle() }}</label>
                  <label class="block h-9">{{ $t('volume').tTitle() }}</label>
                  <label class="block h-9">{{ $t('open_price').tTitle() }}</label>
                  <label class="block h-9">{{ $t('close_price').tTitle() }}</label>
                  <label class="block h-9">{{ $t('swap').tTitle() }}</label>
                  <label class="block h-9">{{ $t('commission').tTitle() }}</label>
                  <label class="block h-9">{{ $t('p_l').tTitle() }}</label>
                </div>
                <div class="ltr:text-right rtl:text-left">
                  <p class="h-9">{{ trade.order }}</p>
                  <p class="h-9">{{ selectedTradesType == 'openTrades' ? trade.open_time_formatted : trade.close_time_formatted }}</p>
                  <p class="h-9">
                    {{
                      selectedTradesType === 'tradeHistory' && platform === 'mt5'
                        ? trade.cmd == '1'
                          ? $t('buy').tTitle()
                          : trade.cmd == '0'
                            ? $t('sell').tTitle()
                            : $t('balance').tTitle()
                        : trade.cmd == '0'
                          ? $t('buy').tTitle()
                          : trade.cmd == '1'
                            ? $t('sell').tTitle()
                            : $t('balance').tTitle()
                    }}
                  </p>
                  <p class="h-9">{{ $simplePair(trade.symbol) }}</p>
                  <p class="h-9">{{ trade.true_volume }}</p>
                  <p class="h-9">{{ trade.open_price }}</p>
                  <p class="h-9">{{ trade.close_price }}</p>
                  <p class="h-9">{{ trade.storage }}</p>
                  <p class="h-9">{{ trade.commission }}</p>
                  <p class="h-9">{{ trade.true_profit.toFixed(2) }}</p>
                </div>
              </div>
              <div class="flex justify-between px-4 pt-[13px] rounded-lg odd:bg-gray-400 overflow-hidden" :class="{ 'max-h-[50px]': !sumsOpen }">
                <div class="text-black font-bold w-full" @click="toggleSums">
                  <label class="block h-9">
                    <img
                      width="12px"
                      height="12px"
                      class="ltr:mr-1 rtl:ml-1 cursor-pointer inline filter grayscale-0 fill-black"
                      :class="{ '-rotate-90': !sumsOpen }"
                      src="../assets/img/platform-dropdown.svg"
                    />
                    {{ $t('sums').tTitle() }}
                  </label>
                  <label class="block h-9">{{ $t('volume').tTitle() }}</label>
                  <label class="block h-9">{{ $t('swap').tTitle() }}</label>
                  <label class="block h-9">{{ $t('commission').tTitle() }}</label>
                  <label class="block h-9">{{ $t('p_l').tTitle() }}</label>
                </div>
                <div class="ltr:text-right rtl:text-left">
                  <p class="h-9"></p>
                  <p class="h-9">{{ sums.true_volume ? sums.true_volume.toFixed(2) : 0 }}</p>
                  <p class="h-9">{{ sums.storage ? sums.storage.toFixed(2) : 0 }}</p>
                  <p class="h-9">{{ sums.commission ? sums.commission.toFixed(2) : 0 }}</p>
                  <p class="h-9">{{ sums.true_profit ? sums.true_profit.toFixed(2) : 0 }}</p>
                </div>
              </div>
              <div class="flex justify-between px-4 pt-[13px] rounded-lg odd:bg-gray-400 overflow-hidden">
                <div class="text-black font-bold">
                  <label class="block h-9">
                    {{ $t('net_total').tTitle() }}
                  </label>
                </div>
                <div class="ltr:text-right rtl:text-left">
                  <p class="h-9">{{ sums.net_total ? sums.net_total.toFixed(2) : 0 }}</p>
                </div>
              </div>
            </div>
          </div>
          <div class="flex flex-col lg:flex-row items-center lg:relative pb-6 lg:pb-12">
            <div class="text-primary-text font-body text-sm leading-10 text-center lg:text-left order-2 lg:order-none mt-2 lg:mt-0 lg:pl-7">
              {{ pagination.total }} {{ $t('records') }}
            </div>
            <div v-if="pagination.total > pagination.rowsPerPage" class="flex justify-center order-1 lg:order-none lg:absolute lg:left-1/2 lg:-translate-x-1/2">
              <pagination-component
                v-if="pagination.total > pagination.rowsPerPage"
                :page="pagination.page"
                :maxPerPage="pagination.rowsPerPage"
                :total="pagination.total"
                :showNavOnMobile="true"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { service } from '../main.js'
import { mapGetters, mapActions } from 'vuex'
import moment from 'moment'
import DatePicker from 'vue2-datepicker'
import PaginationComponent from '../components/Pagination.vue'
import 'vue2-datepicker/index.css'
import { json2csv } from 'json-2-csv'

export default {
  components: {
    DatePicker,
    PaginationComponent,
  },
  props: [],
  data() {
    return {
      modal: {
        active: false,
      },
      pagination: {
        page: 1,
        rowsPerPage: 6,
        total: 0,
      },
      TAID: 0,
      platform: '',
      type: '',
      ndbonus: false,
      selectedTradesType: 'openTrades',
      searchFiltersOpen: false,
      dateFrom: moment(moment().subtract(6, 'months')).format('YYYY-MM-DD'),
      dateTo: moment().format('YYYY-MM-DD'),
      prevFrom: moment(moment().subtract(6, 'months')).format('YYYY-MM-DD'),
      prevTo: moment().format('YYYY-MM-DD'),
      trades: [],
      prevTrades: [],
      sums: {},
      sumsOpen: false,
      prevSums: {},
      tradeFields: {
        order: 'TRADE NO',
        datetimeO: 'open_time',
        datetimeC: 'close_time',
        cmd: 'TYPE',
        symbol: 'SYMBOL',
        true_volume: 'VOLUME',
        open_price: 'OPEN PRICE',
        close_price: 'CLOSE PRICE',
        storage: 'SWAP',
        commission: 'COMMISSION',
        true_profit: 'P&L',
        orderBy: 'date',
        orderMode: 'desc',
      },
      tradeCSVFieldsOpened: [
        { value: 'order', label: 'TRADE NO' },
        { value: 'open_time', label: 'DATETIME' },
        { value: 'cmd', label: 'TYPE' },
        { value: 'symbol', label: 'SYMBOL' },
        { value: 'true_volume', label: 'VOLUME' },
        { value: 'open_price', label: 'OPEN PRICE' },
        { value: 'close_price', label: 'CLOSE PRICE' },
        { value: 'storage', label: 'SWAP' },
        { value: 'commission', label: 'COMMISSION' },
        { value: 'true_profit', label: 'P&L' },
      ],
      tradeCSVFieldsClosed: [
        { value: 'order', label: 'TRADE NO' },
        { value: 'close_time', label: 'DATETIME' },
        { value: 'cmd', label: 'TYPE' },
        { value: 'symbol', label: 'SYMBOL' },
        { value: 'true_volume', label: 'VOLUME' },
        { value: 'open_price', label: 'OPEN PRICE' },
        { value: 'close_price', label: 'CLOSE PRICE' },
        { value: 'storage', label: 'SWAP' },
        { value: 'commission', label: 'COMMISSION' },
        { value: 'true_profit', label: 'P&L' },
      ],
      tradesFormatted: {},
    }
  },
  watch: {
    selectedTradesType() {
      // Reset sums accordion to closed whenever selectedTradesType changes
      this.sumsOpen = false
    },
    'pagination.page'() {
      this.getTradeHistory()
    },
    'modal.active'(value) {
      if (value === true) {
        this.getTradeHistory()
      }
    },
  },
  computed: {
    ...mapGetters({
      User: 'user/all',
      Language: 'language/code',
      logoLoaderActive: 'logoLoader/active',
    }),
  },
  created() {
    this.$bus.$on('pageChanged', p => {
      this.pagination.page = p
      if (this.sumsOpen) this.sumsOpen = false
    })
  },
  mounted() {},
  beforeDestroy() {
    this.$bus.$off('pageChanged', p => {
      this.pagination.page = p
    })
  },
  methods: {
    disabledDate(date) {
      if (this.platform !== 'trl') return false

      const min = moment(date).startOf('day').toDate()
      const max = moment(date).subtract(30, 'days').startOf('day').toDate()

      return date > min || date < max
    },
    disabledDateTo(date) {
      if (this.platform !== 'trl' || (this.platform === 'trl' && this.selectedTradesType !== 'tradeHistory')) return false

      const min = moment(this.dateFrom).startOf('day').toDate()
      const max = moment(this.dateFrom).add(30, 'days').startOf('day').toDate()

      return date < min || date > max
    },

    exportToCSV() {
      // this['logoLoader/set'](true)
      let downloadFunc = function (err, csv) {
        if (err) {
          console.log('error converting csv')
          console.log(err)
          // this['logoLoader/set'](false)
        }
        let a = document.createElement('a')
        let file = new Blob([csv], { type: 'text/csv' })
        a.href = URL.createObjectURL(file)
        a.download = moment().unix() + '-trade_history.csv'
        a.click()
      }
      var csvFields = {}
      if (this.tabsActive == 'openTrades') {
        csvFields = this.tradeCSVFieldsOpened
      } else {
        csvFields = this.tradeCSVFieldsClosed
      }
      let w = trades => {
        this.formatTrades(trades)
        try {
          var endData = json2csv(this.tradesFormatted, {
            checkSchemaDifferences: true,
            keys: csvFields.map(v => v.value),
          })
          downloadFunc('', endData)
        } catch (err) {
          console.error(err)
        }
        // this['logoLoader/set'](false)
      }
      let b = []

      service
        .post('/ta/pamm-trades', {
          userID: this.User.info.ID,
          TAID: this.TAID,
          platform: this.platform,
          type: this.type,
          dateFrom: this.dateFrom + ' 00:00:00',
          dateTo: this.dateTo + ' 23:59:59',
          tradesType: this.selectedTradesType,
        })
        .then(response => {
          if (response.data && response.data.pammTrades) {
            b.push(...response.data.pammTrades)
          }
          w(b)
        })
        .catch(error => {
          // this['logoLoader/set'](false)
          this.$toasted.error(this.$t(error.response.data.meta.messageID))
        })
    },
    formatTrades(data) {
      let keys = Object.keys(data)
      this.tradesFormatted = JSON.parse(JSON.stringify(data))
      let formattedArray = []
      for (let i = 0, l = keys.length; i < l; i++) {
        let allFields = Object.keys(this.tradeFields)
        for (let j = 0, k = allFields.length; j < k; j++) {
          if (typeof this.tradesFormatted[i][allFields[j]] === 'undefined' || this.tradesFormatted[i][allFields[j]] === '-1') {
            this.tradesFormatted[i][allFields[j]] = ''
          }
          if (this.tradesFormatted[keys[i]]['cmd'] == 0) {
            this.tradesFormatted[keys[i]]['cmd'] = this.selectedTradesType === 'tradeHistory' && this.platform === 'mt5' ? 'SELL' : 'BUY'
          } else if (this.tradesFormatted[keys[i]]['cmd'] == 1) {
            this.tradesFormatted[keys[i]]['cmd'] = this.selectedTradesType === 'tradeHistory' && this.platform === 'mt5' ? 'BUY' : 'SELL'
          } else if (this.tradesFormatted[keys[i]]['cmd'] == 6) {
            this.tradesFormatted[keys[i]]['cmd'] = 'BALANCE'
          }
        }
        this.tradesFormatted[keys[i]]['close_time'] =
          this.timestampToDate(this.tradesFormatted[keys[i]]['close_time']) > 0
            ? this.timestampToDate(this.tradesFormatted[keys[i]]['close_time'])
            : this.tradesFormatted[keys[i]]['close_time_formatted']
        this.tradesFormatted[keys[i]]['open_time'] =
          this.timestampToDate(this.tradesFormatted[keys[i]]['open_time']) > 0
            ? this.timestampToDate(this.tradesFormatted[keys[i]]['open_time'])
            : this.tradesFormatted[keys[i]]['open_time_formatted']
        formattedArray.push(this.tradesFormatted[i])
      }
      this.tradesFormatted = formattedArray
    },
    openModal(TAID, platform, type, ndbonus) {
      this.modal.active = true
      this.TAID = TAID
      this.platform = platform
      this.type = type
      this.ndbonus = ndbonus
      this.searchFiltersOpen = false
      document.querySelector('body').classList.add('overflow-hidden')
    },
    closeModal() {
      this.modal.active = false
      this.TAID = 0
      this.platform = ''
      this.type = ''
      this.trades = []
      this.prevTrades = []
      this.sums = { open: false }
      this.prevSums = {}
      this.sumsOpen = false
      this.searchFiltersOpen = false
      this.pagination.page = 1
      document.querySelector('body').classList.remove('overflow-hidden')
    },
    getTradeHistory() {
      // this['logoLoader/set'](true)
      if (this.platform === 'trl' && this.selectedTradesType === 'tradeHistory' && moment(this.dateTo).isAfter(moment(this.dateFrom).add(30, 'days'))) {
        this.dateTo = moment(this.dateFrom).add(30, 'days').format('YYYY-MM-DD')
      }

      service
        .post('/ta/pamm-trades', {
          userID: this.User.info.ID,
          TAID: this.TAID,
          platform: this.platform,
          type: this.type,
          dateFrom: this.dateFrom + ' 00:00:00',
          dateTo: this.dateTo + ' 23:59:59',
          tradesType: this.selectedTradesType,
          page: this.pagination.page,
          recordsperpage: this.pagination.rowsPerPage,
          orderMode: this.tradeFields.orderMode,
          orderBy: this.tradeFields.orderBy,
        })
        .then(response => {
          this.trades = []
          if (response.data.pammTrades) {
            this.trades = response.data.pammTrades
          }
          this.pagination.total = response.data.count

          this.sums.true_volume = response.data.totalVolume
          this.sums.storage = response.data.totalStorage
          this.sums.true_profit = response.data.totalProfit
          this.sums.commission = response.data.totalCommission
          this.sums.active_days = response.data.active_days

          const { true_profit, commission } = this.sums

          if (true_profit === 0 && commission === 0) {
            this.sums.net_total = 0
          } else if (true_profit === 0) {
            this.sums.net_total = commission
          } else if (commission === 0) {
            this.sums.net_total = true_profit
          } else {
            this.sums.net_total = parseFloat(true_profit) + parseFloat(commission)
          }

          // this.changePage()
          // this['logoLoader/set'](false)
        })
        .catch(error => {
          this.$toasted.error(error.response.data.meta ? this.$t(error.response.data.meta.messageID) : error.response.data.detail)
          // this['logoLoader/set'](false)
        })
    },
    timestampToDate(t) {
      return moment.utc(moment.unix(t)).format('YYYY-MM-DD HH:mm')
    },
    changeTab() {
      let tempFrom, tempTo, tempTrades, tempSums

      tempFrom = this.dateFrom
      tempTo = this.dateTo
      tempTrades = this.trades
      tempSums = this.sums

      this.dateFrom = this.prevFrom
      this.dateTo = this.prevTo
      this.trades = this.prevTrades
      this.sums = this.prevSums

      if (this.platform === 'trl' && this.selectedTradesType === 'tradeHistory' && moment(this.dateTo).isAfter(moment(this.dateFrom).add(30, 'days'))) {
        this.dateTo = moment(this.dateFrom).add(30, 'days').format('YYYY-MM-DD')
      }

      this.prevFrom = tempFrom
      this.prevTo = tempTo
      this.prevTrades = tempTrades
      this.prevSums = tempSums
    },
    expandTrade(i) {
      this.$set(this.trades, i, { ...this.trades[i], expanded: !this.trades[i].expanded })
    },
    loadTrades(type) {
      this.selectedTradesType = type
      this.getTradeHistory()
    },
    toggleSums() {
      this.sumsOpen = !this.sumsOpen
    },
    toggleSearchFilters() {
      this.searchFiltersOpen = !this.searchFiltersOpen
    },
    disableBeforeFrom(date) {
      // Disable all dates before the selected "From" date
      if (!this.dateFrom) return false
      const from = new Date(this.dateFrom)
      from.setHours(0, 0, 0, 0)

      const compareDate = new Date(date)
      compareDate.setHours(0, 0, 0, 0)

      // Disable only dates *before* the "From" date — same day stays enabled
      return compareDate < from
    },
    onDateFromChange() {
      // If "To" is earlier than "From", reset it
      if (this.dateTo && this.dateTo < this.dateFrom) {
        this.dateTo = null
      }
    },
    changeOrderBy(orderBy) {
      const isSameColumn = this.tradeFields.orderBy === orderBy

      if (isSameColumn) {
        this.tradeFields.orderMode = this.tradeFields.orderMode === 'desc' ? 'asc' : 'desc'
      }

      this.tradeFields.orderBy = orderBy
      this.getTradeHistory()
    },
    ...mapActions(['logoLoader/set', 'user/setInfo']),
  },
}
</script>

<style>
.mx-input {
  height: 42px;
  border: 0px;
  background-color: rgba(233, 237, 244, var(--tw-bg-opacity));
  -webkit-box-shadow: none;
  box-shadow: none;
}

@media only screen and (max-width: 1024px) {
  .mx-input {
    background-color: white;
  }
}
[dir='rtl'] .mx-icon-calendar,
[dir='rtl'] .mx-icon-clear {
  left: 15px;
  right: auto;
}
</style>
